import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import { Grid } from '@mui/material';

import { BarStack, Bar } from '@visx/shape';
import { Group } from '@visx/group';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { AxisLeft, AxisBottom } from '@visx/axis';
import { scaleBand, scaleLinear, scaleOrdinal } from '@visx/scale';
import _ from 'lodash';

import {
  getParams,
  getMonths,
  thousandFormat,
  thousandFormat2,
  convertMonth,
} from '../../../../Utils/Functions';
import {
  enMonths,
  positiveActiveColor,
  positiveDisableColor,
  negativeActiveColor,
  negativeDisableColor,
  minusActiveColor,
  minusDisableColor,
  activeLabelColor,
} from '../../../../Assets/js/constant';

// accessors
const x = (d) => d.month;

let tooltipTimeout;

const TopChart = ({
  summaryData,
  selectedYears,
  selectedLabels,
  selectedTopItems,
  defaultStartMonth,
  defaultMonth,
  defaultYear,
  handleFilter,
}) => {
  const [resize, setResize] = useState(false);
  const [hoverBar, setHoverBar] = useState(null);
  const [selectedBars, setSelectedBars] = useState([]);
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [colors, setColors] = useState([]);

  const {
    tooltipOpen,
    tooltipData,
    tooltipTop,
    tooltipLeft,
    showTooltip,
    hideTooltip,
  } = useTooltip();

  const handleResize = useCallback(() => {
    setResize(prev => !prev);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [handleResize]);

  const prepareData = useCallback(() => {
    let dictData = {};
    let dictTotals = {};
    let dictBudgets = {};
    let data = [];
    let totals = [];
    let budgets = [];

    let colors = selectedYears.reduce((ret, year) => {
      ret.push(positiveActiveColor);
      return ret;
    }, []);

    let fiscalMonths = getMonths(defaultStartMonth);
    fiscalMonths.forEach((month) => {
      dictData[month] = {};
      dictData[month]['month'] = month;
      dictData[month]['value'] = {};
      dictData[month]['future'] = false;
      selectedYears.forEach((year) => {
        dictData[month][year] = 0;
      });

      dictTotals[month] = 0;
      dictBudgets[month] = 0;
    });

    let _summaryData = _.map(
      _.groupBy(summaryData, 'DimDateKey'),
      function (vals, DimDateKey) {
        return _.reduce(
          vals,
          function (m, o) {
            for (var p in o)
              if (p !== 'DimDateKey') {
                if (
                  p === 'ActualProfit' ||
                  p === 'BudgetProfit' ||
                  p === 'ForecastProfit'
                )
                  m[p] = (parseFloat(m[p]) || 0) + parseFloat(o[p]);
                else m[p] = o[p];
              }

            return m;
          },
          { DimDateKey: DimDateKey },
        );
      },
    );

    _summaryData.forEach((item) => {
      let month = convertMonth(item.MonthName);
      if (dictData[month] !== undefined) {
        if (
          item.DimDateKey >
            (selectedYears[0] - 1) * 10000 +
              (defaultStartMonth - 1) * 100 +
              1 &&
          item.DimDateKey <=
            selectedYears[0] * 10000 +
              (defaultStartMonth - 1) * 100 +
              1
        ) {
          if (!item.ActualFlag) {
            dictData[month][selectedYears[0]] = parseFloat(item.ForecastProfit);
            dictData[month]['future'] = true;
          } else {
            dictData[month][selectedYears[0]] = parseFloat(item.ActualProfit);
            dictData[month]['future'] = false;
          }
          dictData[month]['value'][selectedYears[0]] = item;
          dictData[month]['BudgetProfit'] = parseFloat(item.BudgetProfit);
          dictTotals[month] += dictData[month][selectedYears[0]];
          dictBudgets[month] += parseFloat(item.BudgetProfit);
        }
      }
    });

    fiscalMonths.forEach((month) => {
      data.push(dictData[month]);
      totals.push(dictTotals[month]);
      budgets.push(dictBudgets[month]);
    });

    data.map((d) => {
      if (
        d.future === true &&
        d[selectedYears[0]] === 0 &&
        Object.keys(d.value).length > 0
      ) {
        d[selectedYears[0]] = d.value[selectedYears[0]].BudgetProfit;
        d.value[selectedYears[0]].ForecastProfit =
          d.value[selectedYears[0]].BudgetProfit;
      }
      return d;
    });
    
    setData(data);
    setTotals(totals);
    setBudgets(budgets);
    setColors(colors);
  }, [summaryData, selectedYears, defaultStartMonth]);

  useEffect(() => {
    prepareData();
  }, [prepareData]);

  const getColor = useCallback((bar) => {
    const year = bar.key;
    const month = bar.bar.data.month;

    const actual = bar.bar.data.value[year]
      ? bar.bar.data.value[year].ActualProfit
      : 0;
    const budget = bar.bar.data.value[year]
      ? bar.bar.data.value[year].BudgetProfit
      : 0;
    const forecast = bar.bar.data.value[year]
      ? bar.bar.data.value[year].ForecastProfit
      : 0;

    let activeColor, disableColor;

    if (bar.bar.data.future === false) {
      activeColor = actual < budget ? negativeActiveColor : positiveActiveColor;
      disableColor =
        actual < budget ? negativeDisableColor : positiveDisableColor;
      if (actual < 0) {
        activeColor = minusActiveColor;
        disableColor = minusDisableColor;
      }
    } else {
      activeColor =
        forecast < budget ? negativeActiveColor : positiveActiveColor;
      disableColor =
        forecast < budget ? negativeDisableColor : positiveDisableColor;
      if (forecast < 0) {
        activeColor = minusActiveColor;
        disableColor = minusDisableColor;
      }
    }

    if (selectedTopItems.length === 0 && selectedLabels.length === 0) {
      return activeColor;
    }
    if (selectedLabels.indexOf(month) > -1) return activeColor;

    for (let i = 0; i < selectedTopItems.length; i++) {
      if (
        selectedTopItems[i].month === month &&
        selectedTopItems[i].year === year
      ) {
        return activeColor;
      }
    }

    return disableColor;
  }, [selectedLabels, selectedTopItems]);

  const handleBar = useCallback((event, bar) => {
    let _selectedBars, _selectedLabels, _selectedTopItems;

    const month = bar.bar.data.month;
    const year = month >= defaultStartMonth - 1 ? bar.key - 1 : bar.key;

    if (event.shiftKey) {
      _selectedBars = selectedBars.slice();
      _selectedTopItems = selectedTopItems.slice();
      _selectedLabels = selectedLabels.slice();

      let index = NaN;
      for (let i = 0; i < selectedBars.length; i++) {
        if (selectedBars[i] === event.target) {
          index = i;
          break;
        }
      }

      if (isNaN(index)) {
        event.target.classList.add('barActive');
        _selectedBars.push(event.target);
        _selectedTopItems.push({ month: month, year: year });
        _selectedLabels.push(month);
      } else {
        event.target.classList.remove('barActive');
        _selectedBars.splice(index, 1);
        _selectedTopItems.splice(index, 1);
        _selectedLabels.splice(index, 1);
      }
    } else {
      _selectedLabels = [];
      let exist = false;

      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
        if (selectedBar === event.target) exist = true;
      });

      if (exist && selectedBars.length === 1) {
        _selectedTopItems = [];
        _selectedBars = [];
        _selectedLabels = [];
      } else {
        _selectedTopItems = [{ month: month, year: year }];
        _selectedLabels = [month];
        _selectedBars = [event.target];
        event.target.classList.add('barActive');
      }
    }

    setSelectedBars(_selectedBars);

    handleFilter({
      selectedTopItems: _selectedTopItems,
      selectedLabels: _selectedLabels,
    });
  }, [selectedBars, selectedLabels, selectedTopItems, defaultStartMonth, handleFilter]);

  const handleLabel = useCallback((event, month) => {
    let _selectedMonths, _selectedTopItems, _selectedBars;

    const year =
      month > defaultStartMonth - 1 ? selectedYears[0] - 1 : selectedYears[0];
    
    let index = NaN;
    for (let i = 0; i < selectedLabels.length; i++) {
      if (selectedLabels[i] === month) {
        index = i;
        break;
      }
    }

    if (event.shiftKey) {
      _selectedBars = selectedBars.slice();
      _selectedMonths = selectedLabels.slice();
      _selectedTopItems = selectedTopItems.slice();

      if (isNaN(index)) {
        _selectedMonths.push(month);
        _selectedTopItems.push({ month: month, year: year });
      } else {
        _selectedMonths.splice(index, 1);
        _selectedTopItems.splice(index, 1);
      }
    } else {
      _selectedBars = [];
      _selectedTopItems = [];

      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
      });

      if (!isNaN(index) && selectedLabels.length === 1) {
        _selectedMonths = [];
        _selectedTopItems = [];
      } else {
        _selectedMonths = [month];
        _selectedTopItems.push({ month: month, year: year });
      }
    }

    setSelectedBars(_selectedBars);

    handleFilter({
      selectedLabels: _selectedMonths,
      selectedTopItems: _selectedTopItems,
    });
  }, [selectedBars, selectedLabels, selectedTopItems, selectedYears, defaultStartMonth, handleFilter]);

  const deSelectAll = useCallback(() => {
    if (hoverBar) hoverBar.classList.remove('barHover');

    selectedBars.forEach((selectedBar) => {
      selectedBar.classList.remove('barActive');
    });
    localStorage.removeItem('pfpSelectedTopItems');
    setHoverBar(null);
    setSelectedBars([]);

    handleFilter({
      selectedLabels: [],
      selectedTopItems: [],
    });
  }, [hoverBar, selectedBars, handleFilter]);

  const showTooltipHandler = useCallback((event, xScale, bar, isBar = true) => {
    if (tooltipTimeout) clearTimeout(tooltipTimeout);

    let data = {},
      top,
      left;

    if (isBar) {
      if (hoverBar) hoverBar.classList.remove('barHover');
      event.target.classList.add('barHover');
      setHoverBar(event.target);

      top = event.clientY - 100;
      left =
        window.innerWidth / 2 < event.nativeEvent.layerX
          ? event.nativeEvent.layerX - 170
          : event.nativeEvent.layerX + 50;
      data = bar;
    } else {
      top = event.clientY - 150;
      left =
        window.innerWidth / 2 < event.nativeEvent.layerX
          ? event.nativeEvent.layerX - 200
          : event.nativeEvent.layerX - 100;
      data['budget'] = bar;
    }

    data['isBar'] = isBar;

    showTooltip({
      tooltipData: data,
      tooltipTop: top,
      tooltipLeft: left,
    });
  }, [hoverBar, showTooltip]);

  const hideTooltipHandler = useCallback(() => {
    tooltipTimeout = setTimeout(() => {
      if (hoverBar) hoverBar.classList.remove('barHover');
      setHoverBar(null);

      hideTooltip();
    }, 300);
  }, [hoverBar, hideTooltip]);

  let xTransform = 0;
  let maxYValue = Math.max(
    Math.abs(Math.min(Math.min(...totals), Math.min(...budgets))),
    Math.max(Math.max(...totals), Math.max(...budgets)),
  );
  if (
    !isFinite(maxYValue) ||
    maxYValue === Number.NEGATIVE_INFINITY ||
    isNaN(maxYValue) ||
    maxYValue === 0
  )
    xTransform = 30;
  else {
    if (parseInt(thousandFormat(maxYValue)[0]) > 7)
      xTransform = 8.2 * (thousandFormat2(maxYValue).length + 1) + 8;
    else xTransform = 8.2 * thousandFormat2(maxYValue).length + 8;
  }

  const width = window.innerWidth;
  const height = 160;
  const margin = {
    top: 10,
    right: 0,
    bottom: 20,
    left: xTransform,
  };

  const { pageYOffset, xMax, yMax } = getParams(
    window,
    width,
    height,
    margin,
  );

  const offsetWidth = -1.2;
  let minY = Math.min(0, Math.min(...totals), Math.min(...budgets));
  let maxY = Math.max(0, Math.max(...totals), Math.max(...budgets));

  // scales
  const xScale = scaleBand()
    .domain(data.map(x))
    .rangeRound([0, xMax])
    .padding(0.2);

  const yScale = scaleLinear({
    domain:
      maxY / 3 > Math.abs(minY)
        ? minY >= 0
          ? [0, maxY * 1.3]
          : [-maxY / 3, maxY * 1.3]
        : minY >= 0
        ? [0, maxY * 1.3]
        : [minY * 1.2, maxY * 1.3],
    range: [yMax, 0],
    nice: true,
  });

  const color = scaleOrdinal({
    domain: selectedYears,
    range: colors,
  });

  let fiscalMonths = getMonths(defaultStartMonth);

  let futureLabelx, futureWidth, futureX;
  let pastLabelx, pastWidth;
  let futureCount = data.filter((d) => d.future === true).length;

  if (futureCount === 12) {
    futureLabelx = (width - xTransform - 16) / 2;
    futureWidth = width - xTransform - 16;
    futureX = 0;
  } else if (futureCount === 0) {
    pastLabelx = (width - xTransform - 16) / 2;
    pastWidth = width - xTransform - 16;
  } else {
    futureLabelx =
      xScale.step() * defaultMonth +
      (width - xScale.step() * defaultMonth) / 2 -
      xTransform / 2;
    pastLabelx = (xScale.step() * defaultMonth) / 2;

    futureX =
      (xScale.paddingInner() * xScale.step()) / 2 +
      defaultMonth * xScale.step() -
      3;

    pastWidth =
      defaultMonth === 12
        ? (xScale.paddingInner() * xScale.step()) / 2 +
          defaultMonth * xScale.step() +
          15
        : (xScale.paddingInner() * xScale.step()) / 2 +
          defaultMonth * xScale.step() -
          3;
    futureWidth = width - xTransform - pastWidth - 16;
  }

  if (isNaN(futureLabelx)) futureLabelx = 0;
  if (isNaN(pastLabelx)) pastLabelx = 0;

  return (
    <div>
      <div className="well">
        <div className="subtitle mb-10">Actual vs Forecast Profit</div>

        <Grid container>
          <svg width={width} height={height}>
            <Group top={margin.top} left={margin.left}>
              {futureCount > 0 ? (
                <Group>
                  <Bar
                    width={futureWidth > 0 ? futureWidth : 0}
                    height={height}
                    x={futureX}
                    y={0}
                    fill={
                      selectedYears[0] === defaultYear
                        ? '#f3f3f3'
                        : '#ffffff'
                    }
                    stroke="black"
                    strokeWidth={0}
                  />
                  <text
                    fill="black"
                    dx={futureLabelx - 20}
                    dy={12}
                    fontSize="11px"
                    fontWeight="bold"
                  >
                    Future
                  </text>
                </Group>
              ) : null}
              {12 - futureCount > 0 ? (
                <Group>
                  <Bar
                    width={pastWidth > 0 ? pastWidth : 0}
                    height={height}
                    x={0}
                    y={0}
                    fill="#ffffff"
                    stroke="white"
                    strokeWidth={0}
                  />
                  <text
                    fill="black"
                    dx={pastLabelx}
                    dy={12}
                    fontSize="11px"
                    fontWeight="bold"
                  >
                    Past
                  </text>
                </Group>
              ) : null}
              <rect
                x={0}
                y={0}
                width={width - xTransform > 0 ? width - xTransform : 0}
                height={height}
                fill="transparent"
                onClick={deSelectAll}
              />

              <BarStack
                data={data}
                keys={selectedYears}
                x={x}
                xScale={xScale}
                yScale={yScale}
                color={color}
              >
                {(barStacks) => {
                  return barStacks.map((barStack) => {
                    return barStack.bars.map((bar) => {
                      return (
                        <rect
                          key={`bar-income-${barStack.index}-${bar.index}`}
                          x={bar.x - 6}
                          y={
                            isNaN(bar.height)
                              ? 0
                              : bar.height <= 0
                              ? yScale(0)
                              : bar.y - 1
                          }
                          height={
                            isNaN(bar.height)
                              ? 0
                              : bar.height <= 0
                              ? bar.y - yScale(0)
                              : bar.height + 1
                          }
                          width={bar.width > 0 ? bar.width : 0}
                          fill={getColor(bar)}
                          onClick={(event) => handleBar(event, bar)}
                          onMouseLeave={hideTooltipHandler}
                          onMouseMove={(event) =>
                            showTooltipHandler(event, xScale, bar)
                          }
                          onTouchEnd={hideTooltipHandler}
                          onTouchMove={(event) =>
                            showTooltipHandler(event, xScale, bar)
                          }
                        />
                      );
                    });
                  });
                }}
              </BarStack>

              {budgets.map((budget, index) => {
                return (
                  <Group key={`bar-forecast-${index}`}>
                    <Bar
                      width={
                        (xMax - xScale.paddingInner() * xScale.step()) /
                          fiscalMonths.length +
                        offsetWidth
                      }
                      height={1}
                      x={
                        (xScale.paddingInner() * xScale.step()) / 2 +
                        index * xScale.step() -
                        3
                      }
                      y={yScale(budget) ? yScale(budget) : 0}
                      fill="black"
                      stroke="black"
                      strokeWidth={1}
                      onMouseLeave={hideTooltipHandler}
                      onMouseMove={(event) =>
                        showTooltipHandler(event, xScale, budget, false)
                      }
                      onTouchEnd={hideTooltipHandler}
                      onTouchMove={(event) =>
                        showTooltipHandler(event, xScale, budget, false)
                      }
                    />
                  </Group>
                );
              })}

              <Bar
                width={xMax}
                height={1}
                y={isNaN(yScale(0)) ? 0 : yScale(0)}
              />

              <AxisLeft
                numTicks={4}
                scale={yScale}
                stroke="black"
                tickStroke="black"
                tickLabelProps={(value, index) => ({
                  fill: 'black',
                  fontSize: 11,
                  textAnchor: 'end',
                  dy: '0.33em',
                  dx: '-0.33em',
                })}
                tickComponent={({ formattedValue, ...tickProps }) => (
                  <text {...tickProps} fill="black">
                    ${formattedValue}
                  </text>
                )}
              />

              {fiscalMonths.map((month, index) => {
                return (
                  <rect
                    key={index}
                    x={
                      (xScale.paddingInner() * xScale.step()) / 2 +
                      index * xScale.step()
                    }
                    y={yMax}
                    width={
                      (xMax - (xScale.paddingInner() * xScale.step()) / 2) /
                        fiscalMonths.length +
                      offsetWidth
                    }
                    height={margin.bottom}
                    fill="transparent"
                    onClick={(event) => handleLabel(event, month)}
                  />
                );
              })}

              <AxisBottom
                hideTicks={true}
                scale={xScale}
                top={yMax}
                stroke="black"
                tickStroke="black"
                tickLabelProps={(value, index) => ({
                  fontSize: 11,
                  textAnchor: 'middle',
                  dy: '-0.5em',
                })}
                tickComponent={({ formattedValue, ...tickProps }) => (
                  <text
                    {...tickProps}
                    style={{
                      fontWeight:
                        selectedLabels.indexOf(parseInt(formattedValue)) > -1
                          ? 700
                          : 500,
                    }}
                    fill={
                      selectedLabels.indexOf(parseInt(formattedValue)) > -1
                        ? activeLabelColor
                        : 'black'
                    }                      
                    onClick={(event) =>
                      handleLabel(event, parseInt(formattedValue))
                    }
                  >
                    {enMonths[formattedValue]}
                  </text>
                )}
              />
            </Group>
          </svg>

          {tooltipOpen && (
            <TooltipWithBounds
             
              top={tooltipTop + pageYOffset}
              left={tooltipLeft}
            >
              {tooltipData.isBar ? (
                <div className="bkg-white color-black">
                  <div className="pdv-10">
                    <strong>
                      FY {tooltipData.key} :{' '}
                      {enMonths[x(tooltipData.bar.data)]}
                    </strong>
                  </div>

                  {tooltipData.bar.data.future ? (
                    <div className="ft-12">
                      Forecast:{' '}
                      <strong>
                        {thousandFormat2(
                          tooltipData.bar.data.value[tooltipData.key]
                            .ForecastProfit,
                        )}
                      </strong>
                    </div>
                  ) : (
                    <div className="ft-12">
                      Actual:{' '}
                      <strong>
                        {thousandFormat2(
                          tooltipData.bar.data.value[tooltipData.key]
                            .ActualProfit,
                        )}
                      </strong>
                    </div>
                  )}
                  <div className="ft-12">
                    Budget:{' '}
                    <strong>
                      {thousandFormat2(
                        tooltipData.bar.data.value[tooltipData.key]
                          .BudgetProfit,
                      )}
                    </strong>
                  </div>
                  <br />
                </div>
              ) : (
                <div className="bkg-white color-black">
                  Sum Profit MTD Budget HistoricalOnly ={' '}
                  {thousandFormat2(tooltipData.budget)}
                </div>
              )}
            </TooltipWithBounds>
          )}
        </Grid>
      </div>
    </div>
  );
};

TopChart.propTypes = {
  summaryData: PropTypes.array.isRequired,
  selectedYears: PropTypes.array.isRequired,
  selectedLabels: PropTypes.array.isRequired,
  selectedTopItems: PropTypes.array.isRequired,
  defaultStartMonth: PropTypes.number.isRequired,
  defaultMonth: PropTypes.number.isRequired,
  defaultYear: PropTypes.number.isRequired,
  handleFilter: PropTypes.func.isRequired,
};

export default TopChart;
